import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'

const IS_DEV = import.meta.env.DEV

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Catches rendering errors and shows a recovery screen instead of a white page.
 * Wrap around <Routes> or individual page trees.
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo)
    // Report to Sentry with the React component stack for readable traces.
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  handleGoHome = () => {
    this.setState({ hasError: false, error: null })
    window.location.href = '/home'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: 40,
            backgroundColor: 'var(--bg-page)',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-ui)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h1
            style={{
              fontSize: 20,
              fontWeight: 700,
              margin: '0 0 8px',
              letterSpacing: '-0.02em',
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: 14,
              color: 'var(--text-secondary)',
              margin: '0 0 24px',
              maxWidth: 400,
            }}
          >
            An unexpected error occurred. You can try again or go back to the home page.
          </p>
          {IS_DEV && this.state.error && (
            <details
              style={{
                width: '100%',
                maxWidth: 720,
                marginBottom: 24,
                padding: 16,
                textAlign: 'left',
                backgroundColor: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: 10 }}>
                Error details
              </summary>
              <div style={{ fontSize: 13, marginBottom: 10 }}>
                {this.state.error.message}
              </div>
              {this.state.error.stack && (
                <pre style={{ margin: 0, fontSize: 12, overflowX: 'auto' }}>
                  {this.state.error.stack}
                </pre>
              )}
            </details>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '10px 20px',
                backgroundColor: 'var(--accent-fill)',
                color: 'var(--text-on-accent)',
                border: 'none',
                borderRadius: 50,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                padding: '10px 20px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
                borderRadius: 50,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Go to Home
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
